import { Duration, LocalDateTime, ZoneOffset } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { selectbox } from "trader.ts/ui.common";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import {
  e164,
  messagetemplateid,
  messagetemplatetype,
  queuedmessageid,
  studentid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { checkbox } from "../../client/checkbox";
import { renderDeleteButton, renderSaveButton } from "../../client/savebutton";
import {
  queueableEmail,
  sendableSms,
} from "../../domain/emailtemplates/emailtemplates";
import {
  HHmm,
  ddMM,
  renderFirstNameLastName,
  renderMessageTemplateType,
  wrapInParentheses,
} from "../../render";
import * as routes from "../../routes";
import { renderEditor } from "../groupmessage/editor";
import { renderTableHeaderCellWithSorting } from "../payments_overview_non_periodic/client";

export type queuedMessageFrontend = {
  id: queuedmessageid;
  template_type: messagetemplatetype;
  sms_template_type: null | messagetemplatetype;
  templates: {
    id: messagetemplateid;
    name: string | null;
    is_default: boolean;
  }[];
  smsTemplates: {
    id: messagetemplateid;
    name: string | null;
    is_default: boolean;
  }[];
  content: queueableEmail;
  sms: null | { sms: sendableSms; phone_number: e164 | null };
  created: LocalDateTime;
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
    has_at_least_one_email: boolean;
  };
};
type queuedMessageFrontendWithSources = Omit<queuedMessageFrontend, "sms"> & {
  selectedS: Source<boolean>;
  sms: null | {
    selectedS: Source<boolean>;
    sms: { sms: sendableSms; phone_number: e164 | null };
  };
};

type possibleSorting = "student" | "subject" | "createdon";

export function render(opts: {
  queuedMessages: queuedMessageFrontend[];
}): HTMLElement | HTMLElement[] {
  const sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }> = new Source({ sorting: "createdon", direction: "asc" });

  const emailTemplateSources = makeTemplateSources(
    opts.queuedMessages,
    "email",
  );
  const smsTemplateSources = makeTemplateSources(opts.queuedMessages, "sms");

  const messages: queuedMessageFrontendWithSources[] = opts.queuedMessages.map(
    (qe) => {
      return {
        id: qe.id,
        template_type: qe.template_type,
        sms_template_type: qe.sms_template_type,
        templates: qe.templates,
        smsTemplates: qe.smsTemplates,
        created: qe.created,
        content: qe.content,
        student: qe.student,
        sms: qe.sms
          ? {
              sms: qe.sms,
              selectedS: new Source(false),
            }
          : null,
        selectedS: new Source(false),
      };
    },
  );
  async function handleSelected(
    operation: "remove" | "send",
    messagesToSend: {
      id: queuedmessageid;
      email: boolean;
      template_type: messagetemplatetype;
      sms_template_type: null | messagetemplatetype;
      sms: {
        sendableSms: sendableSms;
        phone_number: e164;
      } | null;
    }[],
  ) {
    await rpc(
      routes.messagequeueUpdateRoute,
      undefined,
      {
        messages: messagesToSend.map((mess) => {
          const templateSourceForThisMess =
            emailTemplateSources.find(
              (t) => t.template_type === mess.template_type,
            ) || null;
          const smsTemplateSourceForThisMess =
            mess.sms && mess.sms_template_type
              ? smsTemplateSources.find(
                  (t) => t.template_type === mess.sms_template_type,
                ) || null
              : null;

          function getMessageTemplateId(
            templateSource:
              | ReturnType<typeof makeTemplateSources>[number]
              | null,
          ): null | messagetemplateid {
            return templateSource === null ||
              templateSource.selected.tag === "no templates defined"
              ? null
              : templateSource.selected.tag === "single template"
              ? templateSource.selected.id
              : templateSource.selected.source.get().id;
          }

          return {
            ...mess,
            sms: mess.sms
              ? {
                  ...mess.sms,
                  sms_messagetemplateid: getMessageTemplateId(
                    smsTemplateSourceForThisMess,
                  ),
                }
              : null,
            messagetemplateid: getMessageTemplateId(templateSourceForThisMess),
          };
        }),
        operation,
      },
      { timeoutMS: Duration.ofMinutes(5).toMillis() },
    );
    document.location.reload();
  }
  const buttonsSig = dynVariadic(
    messages
      .map((m) => m.selectedS)
      .concat(mapPartial(messages, (m) => m.sms?.selectedS || null)),
    (_selected) => {
      const templatesSelected: messagetemplatetype[] = [];
      const smsTemplatesSelected: messagetemplatetype[] = [];

      const messagesToSend = mapPartial(messages, (m) => {
        if (
          m.selectedS.get() === false &&
          (m.sms === null || m.sms.selectedS.get() === false)
        ) {
          return null;
        } else {
          const email =
            m.selectedS.get() && m.student.has_at_least_one_email === true;
          if (m.selectedS.get()) {
            templatesSelected.push(m.template_type);
          }
          const sms =
            m.sms?.selectedS.get() && m.sms.sms.phone_number !== null
              ? {
                  sendableSms: m.sms.sms.sms,
                  phone_number: m.sms.sms.phone_number,
                }
              : null;
          if (
            sms !== null &&
            m.sms_template_type /* should always be the case */
          ) {
            smsTemplatesSelected.push(m.sms_template_type);
          }
          return {
            id: m.id,
            template_type: m.template_type,
            sms_template_type: m.sms_template_type,
            email,
            sms,
          };
        }
      });

      const anySelected = messagesToSend.length > 0;
      const removingS = new Source(false);
      return dyn(removingS, (removing) => {
        const deleteButton = (
          <button
            className={style.button_em_down}
            disabled={!anySelected}
            onclick={() => removingS.set(true)}
          >
            <i className={FA.icon_trash_alt_solid}></i>
            {"Verwijder selectie"}
          </button>
        );
        const numberOfEvaluationsToSend = messagesToSend.filter(
          (m) => m.template_type === "EvaluationFinalized",
        ).length;
        const saveButton = renderSaveButton({
          save: () => handleSelected("send", messagesToSend),
          timeout: 1,
          reload: true,
          disabled: !anySelected,
          icon: FA.icon_check_solid,
          text: {
            normal: "Verstuur selectie",
            success: "Gelukt",
            loading:
              numberOfEvaluationsToSend > 5
                ? "Je verstuurde meerdere evaluaties. Het aanmaken van de PDF's duurt langer, even geduld..."
                : undefined,
          },
        });
        if (removing === false) {
          const templateSelectionWithButtons = renderTemplatesSelection(
            emailTemplateSources,
            templatesSelected.uniq(),
            smsTemplateSources,
            smsTemplatesSelected.uniq(),
          );
          return (
            <div>
              <div style="display: flex; flex-direction: column; align-items: end;">
                {templateSelectionWithButtons}
                <div>
                  {deleteButton}
                  {saveButton}
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div>
              {deleteButton}
              {saveButton}
              <div
                className={style.panel + style.slide_in}
                style="display: inline-block; margin-top: 16px"
              >
                <div>
                  {"Ben je zeker dat je deze berichten niet wilt versturen?"}
                </div>
                <div style="text-align: right">
                  <button
                    className={style.button_em_down}
                    onclick={() => removingS.set(false)}
                  >
                    {"Nee"}
                  </button>
                  {renderSaveButton({
                    save: () => handleSelected("remove", messagesToSend),
                    timeout: 1,
                    reload: true,
                    text: { normal: "Ja", success: "Gelukt" },
                  })}
                </div>
              </div>
            </div>
          );
        }
      });
    },
  );
  function selectAllEmails() {
    const setAllToValue = messages.every((m) => m.selectedS.get() === true)
      ? false
      : true;
    messages
      .filter((m) => m.student.has_at_least_one_email === true)
      .forEach((m) => m.selectedS.set(setAllToValue));
  }
  function selectAllSms() {
    const smsMessages = messages.filter(
      (m) => m.sms && m.sms.sms.phone_number !== null,
    );
    const setAllToValue = smsMessages.every(
      (m) => m.sms?.selectedS.get() === true,
    )
      ? false
      : true;
    messages.forEach((m) => {
      m.sms?.selectedS.set(setAllToValue);
    });
  }

  const selectAllButtons = (
    <article style="text-align: right">
      {messages.some((m) => m.sms !== null) ? (
        <button onclick={selectAllSms}>
          {"Selecteer alle sms'en"}
        </button>
      ) : (
        []
      )}
      <button onclick={selectAllEmails}>
        {"Selecteer alle emails"}
      </button>
    </article>
  );

  if (opts.queuedMessages.length === 0) {
    return (
      <div style="text-align: center">
        {"Er staan geen berichten in de wachtrij"}
      </div>
    );
  } else {
    return (
      <div>
        <article style="text-align: right">{buttonsSig}</article>
        {selectAllButtons}
        <table>
          {dyn(sortingS, (currentSorting) => {
            function renderHeaderCell(
              label: string,
              sorting: possibleSorting | null,
            ) {
              return renderTableHeaderCellWithSorting(
                label,
                sorting,
                currentSorting,
                sortingS,
              );
            }
            return (
              <tr>
                {renderHeaderCell("student", "student")}
                {renderHeaderCell("bericht", "subject")}
                {renderHeaderCell("aangemaakt", "createdon")}
                <th></th>
                <th></th>
              </tr>
            );
          })}
          {dyn(sortingS, (sorting) => {
            return messages
              .orderBy((m) => {
                if (sorting.sorting === "createdon") {
                  return m.created.toEpochSecond(ZoneOffset.UTC);
                } else if (sorting.sorting === "student") {
                  return renderFirstNameLastName(m.student);
                } else if (sorting.sorting === "subject") {
                  return renderMessageTemplateType(m.template_type);
                } else {
                  return checkAllCasesHandled(sorting.sorting);
                }
              }, sorting.direction)
              .flatMap((m) => renderQueuedMessage(emailTemplateSources, m));
          })}
        </table>
      </div>
    );
  }
}

function renderQueuedMessage(
  emailTemplateSources: ReturnType<typeof makeTemplateSources>,
  mess: queuedMessageFrontendWithSources,
): HTMLElement[] {
  const rowSpan = 1;
  const templateSourceForThisMess =
    emailTemplateSources.find((t) => t.template_type === mess.template_type) ||
    null;

  function makeLink(
    messagetemplateid: undefined | messagetemplateid,
    name?: string,
  ) {
    return (
      <a
        href={routes.messagequeueDetailRoute.link({
          identifier: mess.id,
          messagetemplateid,
        })}
        target="_blank"
      >
        {renderMessageTemplateType(mess.template_type)}
        {name ? (
          <div>
            {wrapInParentheses(
              "Sjabloon: %name".replace("%name", name || ""),
            )}
          </div>
        ) : (
          []
        )}
      </a>
    );
  }

  const emailRow = (
    <tr>
      <td rowSpan={rowSpan}>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: mess.student.id,
          })}
        >
          {mess.student.firstname} {mess.student.lastname}
        </a>
      </td>
      <td rowSpan={rowSpan}>
        <b>
          {templateSourceForThisMess === null ||
          templateSourceForThisMess.selected.tag === "no templates defined"
            ? makeLink(undefined)
            : templateSourceForThisMess.selected.tag === "single template"
            ? makeLink(templateSourceForThisMess.selected.id)
            : dyn(templateSourceForThisMess.selected.source, (selected) =>
                makeLink(selected.id, selected.name || ""),
              )}
        </b>
      </td>

      <td>{mess.created.format(ddMM) + " - " + mess.created.format(HHmm)}</td>

      {mess.sms === null ? (
        <td></td>
      ) : (
        <td style="text-align: right">
          <label
            className={style.checkbox + style.input_like}
            style="margin-bottom: 0px;"
          >
            {mess.sms.sms.phone_number === null ? (
              <span>{"Geen SMS nummer gevonden"}</span>
            ) : (
              checkbox({
                source: mess.sms.selectedS,
                label: "SMS" + " (" + mess.sms.sms.phone_number + ")",
              })
            )}
          </label>
        </td>
      )}
      <td style="text-align: right">
        {mess.student.has_at_least_one_email === true ? (
          <label
            className={style.checkbox + style.input_like}
            style="margin-bottom: 0px;"
          >
            {checkbox({ source: mess.selectedS, label: "Email" })}
          </label>
        ) : (
          <div style="display: flex; flex-direction: column">
            <label
              className={style.checkbox + style.input_like}
              style=" margin-bottom: 4px;"
            >
              {"Geen emailadres"}
            </label>
            {renderDeleteButton({
              label: <i className={FA.icon_trash_alt_solid}></i>,
              className: style.button_has_only_icon + style.button_small_blue,
              dontShowLabelAgainInConfirmation: true,
              containerStyle: "align-items: stretch;",
              confirmationLabel:
                "Ben je zeker dat je deze email wilt verwijderen?",
              doDelete: () =>
                rpc(routes.messagequeueUpdateRoute, undefined, {
                  messages: [
                    {
                      id: mess.id,
                      email: false,
                      sms: null,
                      messagetemplateid: null,
                    },
                  ],
                  operation: "remove",
                }),
              redirectTo: routes.messagequeueRoute.link(),
            })}
          </div>
        )}
      </td>
    </tr>
  );

  return [emailRow];
}

export function renderDetail(opts: {
  id: queuedmessageid;
  title: string;
  body: string;
  student_has_email_addresses: boolean;
}): HTMLElement | HTMLElement[] {
  const domParser = new DOMParser();
  const parsed = domParser.parseFromString(opts.body, "text/html");

  const editingS = new Source(false);

  return dyn(editingS, (editing) => {
    if (editing === false) {
      const container = <div></div>;
      const shadow = container.attachShadow({ mode: "closed" });
      Array.from(parsed.head.getElementsByTagName("style")).forEach((st) => {
        shadow.appendChild(st);
      });
      shadow.appendChild(parsed.body);
      return (
        <div>
          <section>{container}</section>
          {opts.student_has_email_addresses === true ? (
            <div style="text-align: right">
              <button
                className={style.button_accent}
                onclick={() => editingS.set(true)}
              >
                <i className={FA.icon_pencil_alt_solid}></i>
                {"Pas aan"}
              </button>
            </div>
          ) : (
            <span></span>
          )}
        </div>
      );
    } else {
      const htmlS = new Source(opts.body);
      const editor = renderEditor(htmlS, null, null, {
        style: `min-height: 20rem; border: 1px solid var(--grey-150); `,
        className: " ",
      });
      const container = <div></div>;
      // We put it into a shadow DOM so it doesn't interfere with styles outside of the email
      const shadow = container.attachShadow({ mode: "closed" });
      shadow.appendChild(editor.editablediv);
      return (
        <div>
          {editor.buttonSection}
          {container}

          <div style="text-align: right; margin-top: 16px;">
            {renderSaveButton({
              save: () => {
                return rpc(
                  routes.sendEmailFromMessageQueueCustomSpec,
                  undefined,
                  { body: htmlS.get(), id: opts.id, title: opts.title },
                );
              },
              text: {
                normal: "Verstuur aangepaste email",
              },
              reload: () =>
                (document.location = routes.messagequeueRoute.link()),
            })}
          </div>
        </div>
      );
    }
  });
}

function makeTemplateSources(
  queuedMessages: queuedMessageFrontend[],
  tag: "email" | "sms",
): {
  template_type: messagetemplatetype;
  selected:
    | { tag: "no templates defined" }
    | { tag: "single template"; id: messagetemplateid }
    | {
        tag: "multiple templates";
        possibilities: {
          template_type: messagetemplatetype;
          id: messagetemplateid;
          name: string | null;
          is_default: boolean;
        }[];
        source: Source<{
          template_type: messagetemplatetype;
          id: messagetemplateid;
          name: string | null;
          is_default: boolean;
        }>;
      };
}[] {
  const allTemplateTypesInQueue: messagetemplatetype[] = [];
  const allTemplatesForMessagesInQueue = queuedMessages
    .flatMap((q) => {
      allTemplateTypesInQueue.push(q.template_type);
      if (tag === "email") {
        return q.templates.map((t) => ({
          ...t,
          template_type: q.template_type,
        }));
      } else if (tag === "sms") {
        if (q.sms_template_type !== null) {
          const template_type = q.sms_template_type;
          return q.smsTemplates.map((t) => ({
            ...t,
            template_type,
          }));
        } else {
          return [];
        }
      } else {
        return checkAllCasesHandled(tag);
      }
    })
    .uniqBy((t) => t.id);

  return allTemplateTypesInQueue.uniq().map((template_type) => {
    const possibilities = allTemplatesForMessagesInQueue
      .filter((t) => t.template_type === template_type)
      .orderBy(
        [
          (t) => (t.is_default ? 1 : 0),
          (t) =>
            (t.is_default ? "Standaard" + ": " : "") + t.name || "",
        ],
        ["desc", "asc"],
      );
    return {
      template_type,
      selected:
        possibilities.length === 0
          ? { tag: "no templates defined" }
          : possibilities.length === 1
          ? { tag: "single template", id: possibilities[0].id }
          : {
              tag: "multiple templates",
              source: new Source(possibilities[0]),
              possibilities: possibilities,
            },
    };
  });
}

function renderTemplatesSelection(
  emailTemplateSources: ReturnType<typeof makeTemplateSources>,
  templatesToShowForSelection: messagetemplatetype[],
  smsTemplateSources: ReturnType<typeof makeTemplateSources>,
  smsTemplatesToShowForSelection: messagetemplatetype[],
): HTMLElement {
  const renderedEmailTemplates = mapPartial(templatesToShowForSelection, (t) =>
    renderSingleTemplateType(emailTemplateSources, t),
  );

  const renderedSmsTemplates = mapPartial(smsTemplatesToShowForSelection, (t) =>
    renderSingleTemplateType(smsTemplateSources, t),
  );

  if (renderedEmailTemplates.length + renderedSmsTemplates.length > 0) {
    return (
      <div>
        <div>
          {renderedEmailTemplates}
          {renderedSmsTemplates}
        </div>
      </div>
    );
  } else {
    return <span></span>;
  }
}

function renderSingleTemplateType(
  templateSources: ReturnType<typeof makeTemplateSources>,
  type: messagetemplatetype,
) {
  const templateSource = templateSources.find(
    (ts) => ts.template_type === type,
  );
  if (!templateSource) {
    return null;
  }
  if (
    templateSource.selected.tag === "no templates defined" ||
    templateSource.selected.tag === "single template"
  ) {
    return null;
  }

  const sel = templateSource.selected;
  const textSelectValues = sel.possibilities.map((p) => ({
    label: (p.is_default ? "Standaard" + ": " : "") + (p.name || ""),
    value: p.id.toString(),
  }));
  const textSource = new Source(textSelectValues[0].value.toString());
  scheduleForCleanup(
    textSource.observe((selectedIdAsText) => {
      const found = sel.possibilities.find(
        (p) => p.id.toString() === selectedIdAsText,
      );
      if (found) {
        sel.source.set(found);
      }
    }),
  );

  return (
    <div style="width: 416px;">
      {selectbox({
        source: textSource,
        options: textSelectValues,
        label: "Sjabloon voor %name".replace(
          "%name",
          renderMessageTemplateType(templateSource.template_type),
        ),
      })}
    </div>
  );
}
